// Cache-busting reload for stale-bundle recovery in the MobiLoud/Canvas webview.
//
// A plain window.location.reload() is served from the webview's HTTP cache, so a
// stale index.html that references deleted post-deploy JS chunk hashes reloads
// into the SAME dead document and stays blank. This helper forces a fresh fetch:
// it best-effort wipes the Cache Storage API, then navigates to the current URL
// with a throwaway `_cb` query param (which the server ignores) so the request
// misses every cache layer. It uses location.replace, not assign, so recovery
// does not leave a junk history entry.
//
// Every step is wrapped so this can NEVER throw on a boot path shared by every
// user.
//
// The `location`/`cacheStorage` params default to the real globals, so callers
// invoke `cacheBustingReload()` with no args. They exist ONLY so tests can inject
// fakes: under jsdom 26 `window.location` and its methods are non-configurable
// and non-writable, so they cannot be spied any other way (see hard_reload.js).

// Pure: returns a cache-busted copy of `href`. Strips any existing `_cb` first so
// repeated recoveries don't accumulate the param. Exported for unit testing.
export const buildCacheBustUrl = (href) => {
  try {
    const url = new URL(href);
    url.searchParams.delete("_cb");
    url.searchParams.set("_cb", String(Date.now()));
    return url.toString();
  } catch {
    // Unparseable href: fall back to the original so the caller still navigates.
    return href;
  }
};

// Best-effort wipe of the Cache Storage API. Fire-and-forget and fully wrapped:
// a hung or rejected caches promise must never block or break the reload.
export const clearCacheStorage = (cacheStorage) => {
  try {
    if (cacheStorage?.keys) {
      cacheStorage
        .keys()
        .then((keys) => keys.forEach((key) => cacheStorage.delete(key)))
        .catch(() => {});
    }
  } catch {
    // Cache Storage unavailable or blocked (private mode / webview): ignore.
  }
};

export const cacheBustingReload = ({
  location = typeof window !== "undefined" ? window.location : undefined,
  cacheStorage = typeof window !== "undefined" ? window.caches : undefined,
} = {}) => {
  clearCacheStorage(cacheStorage);

  try {
    location.replace(buildCacheBustUrl(location.href));
  } catch {
    // Last resort: nothing more we can safely do without risking a throw on
    // the boot path.
  }
};

export default cacheBustingReload;
